/** Weapon variants share one atlas and one skin/draw with the body on every quality tier. */
import { Mesh, MeshBuilder, SubMesh, Vector3, VertexData } from '@babylonjs/core/pure';
import type { Geometry, PBRMaterial, Scene } from '@babylonjs/core/pure';
import type { CharacterGeometry, CharacterPartRange } from './actors';
import { remapUVs } from './soldierAtlas';
import { RIG_SOLDIER, SOLDIER_PART_BONE } from './anim/rigs';
import { bindRigidSkin, buildSkeleton } from './anim/skeletal';

export const SOLDIER_WEAPONS = ['Sidearm', 'Rifle', 'Minigun', 'Bazooka'] as const;
/** Model-space muzzle sockets; the weapon bone transforms these with the visible geometry. */
export const SOLDIER_MUZZLES: readonly (readonly [number, number, number])[] = [
  [.075, 1.29, .69], [.075, 1.194, .957], [.075, 1.29, 1.12], [.075, 1.36, 1.1],
];

export function weaponTier(tier: number): number {
  return Math.max(0, Math.min(SOLDIER_WEAPONS.length - 1, Math.floor(tier)));
}

function variant(scene: Scene, source: CharacterGeometry, material: PBRMaterial, tier: number): CharacterGeometry {
  const mesh = new Mesh(`soldier-weapon-source-${tier}`, scene);
  const positions: number[] = [], normals: number[] = [], uvs: number[] = [], indices: number[] = [], colors: number[] = [];
  const parts: CharacterPartRange[] = [];
  const original = VertexData.ExtractFromMesh(source.mesh);
  const oldIndices = original.indices!;
  for (const part of source.parts) {
    if (part.name.startsWith('rifle')) continue;
    const start = positions.length / 3;
    for (let v = part.vertexStart; v < part.vertexEnd; v++) {
      positions.push(...original.positions!.slice(v * 3, v * 3 + 3));
      normals.push(...original.normals!.slice(v * 3, v * 3 + 3));
      uvs.push(...original.uvs!.slice(v * 2, v * 2 + 2));
      colors.push(...(original.colors?.slice(v * 4, v * 4 + 4) ?? [1, 1, 1, 1]));
    }
    for (let i = 0; i < oldIndices.length; i += 3) {
      if (oldIndices[i]! >= part.vertexStart && oldIndices[i]! < part.vertexEnd) {
        for (let j = 0; j < 3; j++) indices.push(start + oldIndices[i + j]! - part.vertexStart);
      }
    }
    parts.push({ ...part, vertexStart: start, vertexEnd: positions.length / 3 });
  }
  const pieces: Mesh[] = [];
  const box = (w: number, h: number, d: number, x: number, y: number, z: number): void => {
    const m = MeshBuilder.CreateBox('weapon-piece', { width: w, height: h, depth: d }, scene);
    m.position.set(x, y, z); pieces.push(m);
  };
  const tube = (radius: number, length: number, x: number, y: number, z: number, sides = 6): void => {
    const m = MeshBuilder.CreateCylinder('weapon-piece', { diameter: radius * 2, height: length, tessellation: sides }, scene);
    m.rotation.x = Math.PI / 2; m.position.set(x, y, z); pieces.push(m);
  };
  if (tier === 0) {
    box(.1, .12, .27, .075, 1.29, .51);
    box(.085, .17, .1, .075, 1.19, .43);
    tube(.035, .12, .075, 1.29, .63);
  } else if (tier === 2) {
    box(.22, .2, .27, .075, 1.29, .48);
    box(.24, .2, .2, .075, 1.1, .47);
    for (let i = 0; i < 6; i++) {
      const a = i * Math.PI / 3;
      tube(.026, .52, .075 + Math.cos(a) * .066, 1.29 + Math.sin(a) * .066, .86);
    }
  } else {
    tube(.115, .86, .075, 1.36, .67, 8);
    tube(.145, .1, .075, 1.36, .23, 8);
    box(.09, .2, .12, .075, 1.18, .43);
  }
  const start = positions.length / 3;
  const firstIndex = indices.length;
  for (const piece of pieces) {
    remapUVs(piece, 'weapon');
    const vd = VertexData.ExtractFromMesh(piece);
    const transform = piece.computeWorldMatrix(true);
    const base = positions.length / 3;
    for (let v = 0; v < vd.positions!.length; v += 3) {
      const p = Vector3.TransformCoordinates(Vector3.FromArray(vd.positions!, v), transform);
      const n = Vector3.TransformNormal(Vector3.FromArray(vd.normals!, v), transform);
      positions.push(p.x, p.y, p.z); normals.push(n.x, n.y, n.z); colors.push(1, 1, 1, 1);
    }
    uvs.push(...vd.uvs!); indices.push(...Array.from(vd.indices!, i => i + base));
    piece.dispose();
  }
  parts.push({ name: 'rifleReceiver', tile: 'weapon', vertexStart: start, vertexEnd: positions.length / 3, triangles: (indices.length - firstIndex) / 3 });
  const data = new VertexData();
  Object.assign(data, { positions, normals, uvs, colors, indices }); data.applyToMesh(mesh);
  mesh.material = material;
  return { mesh, parts };
}

export class SoldierWeaponVariants {
  private readonly variants: CharacterGeometry[];
  private tier = 1;
  private readonly geometries: Geometry[];
  constructor(scene: Scene, material: PBRMaterial, private readonly source: CharacterGeometry) {
    this.variants = [variant(scene, source, material, 0), source, variant(scene, source, material, 2), variant(scene, source, material, 3)];
    this.geometries = this.variants.map(v => v.mesh.geometry!);
    for (const [i, geometry] of this.variants.entries()) {
      if (i === 1) continue;
      const handle = buildSkeleton(scene, RIG_SOLDIER, `weapon-variant-${i}`);
      bindRigidSkin(geometry.mesh, handle, geometry.parts, RIG_SOLDIER, name => SOLDIER_PART_BONE[name]!);
      geometry.mesh.setEnabled(false);
    }
  }
  /** Changes geometry only: placement, skeleton, material and baked animation remain owned by caller. */
  applyTo(mesh: Mesh, tier: number): void {
    // Babylon stores thin-instance attributes on geometry; carry them across the swap.
    const instanceBuffers = mesh.getVerticesDataKinds().map(kind => mesh.getVertexBuffer(kind))
      .filter(buffer => buffer?.getIsInstanced());
    this.geometries[weaponTier(tier)]!.applyToMesh(mesh);
    for (const buffer of instanceBuffers) {
      if (buffer && mesh.getVertexBuffer(buffer.getKind()) !== buffer) mesh.setVerticesBuffer(buffer);
    }
    // Plan 32: weapon swaps only GROW the geometry, and Babylon 9.28's `_createGlobalSubMesh`
    // reuses a submesh unless it OVERRUNS the new totals — so every pickup kept the previous
    // variant's draw range (probe-confirmed: the minigun drawn with the sidearm's 2,928-index
    // range, its final 120 triangles never drawn). Babylon's own mesh routines rebuild after
    // `releaseSubMeshes()`; do the same so every tier gets the exact full range.
    mesh.releaseSubMeshes();
    SubMesh.AddToMesh(0, 0, mesh.getTotalVertices(), 0, mesh.getTotalIndices(), mesh);
    mesh.refreshBoundingInfo();
  }
  setTier(tier: number): void {
    const next = weaponTier(tier);
    if (next === this.tier) return;
    this.applyTo(this.source.mesh, next); this.tier = next;
  }
}
